import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useLayoutEffect
} from "react";

import * as d3 from "d3";
import PropTypes from "prop-types";

import { usePalette } from "Libs/theme";
import useUniqueId from "Libs/useUniqueId";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import useMetricsContext from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import TimeSeek from "src/organization/pages/project/environment/metrics/http/components/timeseek";
import { useCursorDataSync } from "src/organization/pages/project/environment/metrics/http/helper";
import {
  getDefaultDomain,
  getLinearAxis
} from "src/organization/pages/project/environment/metrics/http2/helper";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";
import useActivityWorker from "src/organization/pages/project/environment/metrics/utils/workers/activityworker/useActivityWorker";

import Activity from "../activities";

import applyBrush from "./brush";
import * as S from "./Chart.styles";
import LineGradient from "./LineGradient";
import Lines from "./Lines";
import { hashTimeFrame, margin, timeframeToRange } from "./settings";
import Thresholds from "./Thresholds";
import { getOverCommitInfo, getGradient } from "./thresholdUtils";
import Tooltip from "./Tooltip";
import { useUpdateResourceDimension } from "./utils";

const getDomains = (max, timeframe, data) => {
  const defaultDomain = getDefaultDomain(timeframe);
  const domains = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, max]
  };
  return data ? domains : defaultDomain;
};

const Chart = ({
  onBrush,
  data,
  hosts,
  serviceKind,
  yFormatter = v => v,
  tooltipFormatter = v => v,
  enableOvercommit = false,
  service,
  unit
}) => {
  const { getThreshold } = useAutoscallingValues(service?.name);
  const autoscaleConfig = useMemo(
    () => getThreshold({ service, serviceUnit: unit }),
    [service?.name, unit]
  );

  const id = useUniqueId().toString();
  const theme = usePalette(S.colorDefinitions);
  const brushRef = useRef(null);
  const xAxisRef = useRef();
  const { isDeployment, timeFrame, activityConfig } = useMetricsContext();
  const { group } = useActivityWorker({
    timeFrame,
    isDeployment,
    activityConfig
  });

  const [isBrushing, setIsBrushing] = useState();
  const [activeLine, setActiveLine] = useState();

  const range = useMemo(() => timeframeToRange(timeFrame), [timeFrame]);

  const { max, lineGradient, overCommitInfo } = useMemo(() => {
    const overCommitInfo = getOverCommitInfo(data);
    const lineGradient = getGradient(
      overCommitInfo.fixedMax,
      overCommitInfo.reportedMax
    );
    return {
      max: overCommitInfo.max,
      lineGradient,
      overCommitInfo
    };
  }, [data]);

  const dimension = useUpdateResourceDimension(id);
  const { xDomain, yDomain } = useMemo(
    () => getDomains(max, timeFrame, data),
    [timeFrame, data, max]
  );

  const {
    xScale: xAxis,
    yScale: yAxis,
    positions
  } = useMemo(
    () =>
      getLinearAxis({
        dimension: dimension,
        xDomain,
        yDomain
      }),
    [dimension, xDomain, yDomain]
  );

  useEffect(() => {
    const xticks = getXTickCount(dimension.width, range);
    const axisBottom = d3
      .axisBottom(xAxis)
      .ticks(xticks?.count)
      .tickFormat(xticks.format);

    const axisGroup = d3.select(xAxisRef.current).call(axisBottom);

    axisGroup.selectAll(".tick").each(function (d) {
      // note: the use of function literal is intentional here
      // so that d3 can bind the data to this keyword
      d3.select(this)
        .select("text")
        .classed("new-day-start", new Date(d).getHours() === 0);
    });
  }, [positions.xRangeEnd, positions.xRangeStart, positions.xTransform, xAxis]);

  const brushMove = useCallback(event => {
    if (event.selection) {
      setIsBrushing(true);
    }
  }, []);

  const brushEnd = useCallback(
    (start, end) => {
      onBrush(hashTimeFrame({ start, end }));
      setIsBrushing(false);
    },
    [onBrush]
  );

  useLayoutEffect(() => {
    const brushBehavior = applyBrush(xAxis, yAxis, brushEnd, brushMove);
    const brushGroup = d3.select(brushRef.current);
    brushGroup.call(brushBehavior);
  }, [yAxis, xAxis]);

  const cursorFuncProps = useMemo(
    () => ({
      xScale: xAxis,
      yScale: yAxis,
      data,
      bisectFn: d => d.timestamp,
      disable: !data,
      chartId: id
    }),
    [id, xAxis, xAxis]
  );

  const { cursorSync, handleCursorMove } = useCursorDataSync({
    cursorFuncProps
  });

  const adaptedCursor = cursorSync?.adaptedCursor;

  const CHART_HEIGHT = dimension.height;
  return (
    <S.Layout>
      <S.Svg
        height={CHART_HEIGHT}
        id={`${id}-chart`}
        onMouseMove={handleCursorMove}
        onMouseUp={() => handleCursorMove?.(null)}
        onMouseLeave={() => handleCursorMove?.(null)}
      >
        <defs>
          <linearGradient id={`gradient-${id}`}>
            <stop offset="0" stopColor="transparent" />
            <stop offset="12%" stopColor="white" />
          </linearGradient>
          <mask
            id={`mask-${id}`}
            maskContentUnits="userSpaceOnUse"
            maskUnits="userSpaceOnUse"
          >
            <rect width="100%" height="100%" fill={`url(#gradient-${id})`} />
          </mask>
          <LineGradient
            enableOvercommit={enableOvercommit}
            kind="average"
            id={`average-fire-${id}`}
            y1={CHART_HEIGHT - margin.bottom}
            y2={margin.top}
            threshold80={lineGradient[0]}
            threshold90={lineGradient[1]}
          />
          <LineGradient
            enableOvercommit={enableOvercommit}
            kind="host"
            id={`host-fire-${id}`}
            y1={CHART_HEIGHT - margin.bottom}
            y2={margin.top}
            threshold80={lineGradient[0]}
            threshold90={lineGradient[1]}
          />
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={`overcommit-${id}`}
            x1="0"
            y1={CHART_HEIGHT - margin.bottom}
            x2="0"
            y2={margin.top}
          >
            <stop
              offset={`${lineGradient[2]}%`}
              stopOpacity=".5"
              stopColor="transparent"
            />
            <stop
              offset={`${lineGradient[2]}%`}
              stopOpacity=".5"
              stopColor={theme.threshold__max}
            />
          </linearGradient>
        </defs>
        <S.Brush ref={brushRef} data-role="brush-container" />
        {group.map((g, index) => (
          <Activity
            key={`${g.type}-${index}`}
            type={g.type}
            activities={g.activities}
            xAxis={xAxis}
            yAxis={yAxis}
          />
        ))}
        {!isBrushing && adaptedCursor?.canSyncVisible && (
          <TimeSeek yScale={yAxis} cursorData={adaptedCursor} />
        )}

        <S.XAxis
          ref={xAxisRef}
          transform={positions.xTransform}
          data-role="x-axis"
        />
        <Thresholds
          data={data}
          xAxis={xAxis}
          yAxis={yAxis}
          chartId={id}
          overCommitInfo={overCommitInfo}
          serviceKind={serviceKind}
          yFormatter={yFormatter}
          autoscaleConfig={autoscaleConfig}
          resourceName={unit}
          enableOvercommit={enableOvercommit}
        />
        {xAxis && yAxis && (
          <Lines
            enableOvercommit={enableOvercommit}
            chartId={id}
            data={data}
            hosts={hosts}
            xAxis={xAxis}
            yAxis={yAxis}
            onActiveChange={setActiveLine}
            activeLine={activeLine}
          />
        )}
      </S.Svg>
      {adaptedCursor?.tooltipIsPositionable && (
        <Tooltip
          cursorData={cursorSync}
          isVisible={adaptedCursor.indexedData}
          hosts={hosts}
          activeLine={activeLine}
          tooltipFormatter={tooltipFormatter}
        />
      )}
    </S.Layout>
  );
};

Chart.propTypes = {
  service: PropTypes.object,
  data: PropTypes.array.isRequired,
  hosts: PropTypes.arrayOf(PropTypes.string.isRequired),
  yFormatter: PropTypes.func.isRequired,
  tooltipFormatter: PropTypes.func,
  onBrush: PropTypes.func,
  serviceKind: PropTypes.string,
  unit: PropTypes.string,
  enableOvercommit: PropTypes.bool
};
export default React.memo(Chart);
